import { placeMultiple } from "app/api/multiplesBetsApi";
import { constructPayload } from "app/helpers/betBuilderHelper";
import { SessionHelper } from "app/helpers/sessionHelper";
import { BetBuilderEventsType } from "app/types/betBuilderType";
import { BetSlipStates, OddsType } from "app/utils/const";
import { debounce, get } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import * as api from "../../api/multiplesBetsApi";
import type { BetSlipType } from "../../types/betSlipType";

type BetBuilderState = {
  betBuilderEvents: BetBuilderEventsType;
};

const BetBuilderModel = {
  state: {
    betBuilderEvents: {},
  } as BetBuilderState,
  reducers: {
    addBetBuilderItem(state: BetBuilderState, payload: BetSlipType) {
      const eventId = payload.id;

      // If adding a new item from an event that is currently showing a success message,
      // (ie there is a betBuilder with status "FINISHED"),
      // then we need to first remove the event associated with the finished bet.
      // Success message is removed from the bet slip, and the new item is added.
      if (state.betBuilderEvents[eventId]?.status === BetSlipStates.FINISHED) {
        const remainingEvents = { ...state.betBuilderEvents };
        delete remainingEvents[eventId];
        state = {
          ...state,
          betBuilderEvents: remainingEvents,
        };
      }

      // If betBuilder event already created then use that
      // if not then create a new one
      let currentEvent;
      if (state.betBuilderEvents[eventId]) {
        currentEvent = state.betBuilderEvents[eventId];
        // Set status and message to empty strings
        currentEvent.status = "";
        currentEvent.message = "";
        currentEvent.submitting = false;
      } else {
        currentEvent = {
          eventId,
          eventName: payload.name,
          sportType: payload.sportType,
          betBuilderItems: [],
          isCollapsibleOpen: true,
          status: "",
          message: "",
          submitting: false,
        };
      }
      // Check if the runner has already been added
      const existingSelection = currentEvent.betBuilderItems.find(
        betSlip =>
          betSlip.markets[0].runners[0].id === payload.markets[0].runners[0].id
      );

      // Don't add to betbuilder
      if (existingSelection) {
        return {
          ...state,
          betBuilderEvents: {
            ...state.betBuilderEvents,
            [eventId]: currentEvent,
          },
        };
      }

      const localId =
        currentEvent.betBuilderItems.length === 0
          ? 0
          : Math.max(...currentEvent.betBuilderItems.map(b => b.localId)) + 1;

      const selection = cloneDeep(payload);
      const runnerId = get(payload, ["markets", 0, "runners", 0, "id"]);

      selection.localId = localId;
      if (runnerId) {
        selection.runnerId = runnerId;
      }

      const updatedBetBuilderItems = [
        ...currentEvent.betBuilderItems,
        selection,
      ];

      const updatedBetBuilderEvents = {
        ...state.betBuilderEvents,
        [eventId]: {
          ...currentEvent,
          betBuilderItems: updatedBetBuilderItems,
        },
      };

      return {
        ...state,
        betBuilderEvents: updatedBetBuilderEvents,
      };
    },
    removeBetBuilderItem(state: BetBuilderState, payload: BetSlipType) {
      const eventId = payload.id;
      const currentEvent = state.betBuilderEvents[eventId];

      if (!currentEvent) {
        return state;
      }

      // Filter out the item to be removed
      const remainingItems = currentEvent.betBuilderItems.filter(
        item => item.localId !== payload.localId
      );

      // If no items left, remove the event
      if (remainingItems.length === 0) {
        const events = { ...state.betBuilderEvents };
        delete events[eventId];
        return {
          ...state,
          betBuilderEvents: events,
        };
      }

      const updatedEvent = {
        ...currentEvent,
        betBuilderItems: remainingItems,

        status: "",
        message: "",
      };

      const updatedBetBuilderEvents = {
        ...state.betBuilderEvents,
        [eventId]: updatedEvent,
      };

      return {
        ...state,
        betBuilderEvents: updatedBetBuilderEvents,
      };
    },
    removeRedundantBetBuilderItems(
      state: BetBuilderState,
      payload: { eventId: number; itemsToRemove: BetSlipType[] }
    ) {
      const { eventId, itemsToRemove } = payload;
      const currentEvent = state.betBuilderEvents[eventId];

      if (!currentEvent) {
        return state;
      }

      // Filter out the items to be removed
      const remainingItems = currentEvent.betBuilderItems.filter(
        item =>
          !itemsToRemove.some(toRemove => toRemove.localId === item.localId)
      );

      if (remainingItems.length === 0) {
        const events = { ...state.betBuilderEvents };
        delete events[eventId];
        return {
          ...state,
          betBuilderEvents: events,
        };
      }

      return {
        ...state,
        betBuilderEvents: {
          ...state.betBuilderEvents,
          [eventId]: { ...currentEvent, betBuilderItems: remainingItems },
        },
      };
    },

    removeBet(state: BetBuilderState, payload: { eventId: number }) {
      const eventIdToRemove = payload.eventId;

      const remainingEvents = {};
      for (const eventId in state.betBuilderEvents) {
        if (state.betBuilderEvents[eventId].eventId !== eventIdToRemove) {
          remainingEvents[eventId] = state.betBuilderEvents[eventId];
        }
      }

      return {
        ...state,
        betBuilderEvents: remainingEvents,
      };
    },
    removeAll(state: BetBuilderState) {
      return {
        ...state,
        betBuilderEvents: {},
      };
    },
    setValidationRequest(state: Record<string, any>): any {
      return {
        ...state,
        loading: true,
      };
    },
    setValidationError(state: Record<string, any>): any {
      return {
        ...state,
        loading: false,
      };
    },
    setCollapsibleClosed(state: BetBuilderState, payload: number) {
      const eventToUpdate = cloneDeep(state.betBuilderEvents[payload]);
      eventToUpdate.isCollapsibleOpen = false;
      return {
        ...state,
        betBuilderEvents: {
          ...state.betBuilderEvents,
          [payload]: eventToUpdate,
        },
      };
    },
    setCollapsibleOpen(state: BetBuilderState, payload: number) {
      const eventToUpdate = cloneDeep(state.betBuilderEvents[payload]);
      eventToUpdate.isCollapsibleOpen = true;
      return {
        ...state,
        betBuilderEvents: {
          ...state.betBuilderEvents,
          [payload]: eventToUpdate,
        },
      };
    },
    collapseAllCollapsibles(state: BetBuilderState) {
      const eventsToUpdate = cloneDeep(state.betBuilderEvents);
      Object.values(eventsToUpdate).forEach(event => {
        event.isCollapsibleOpen = false; // Collapse all events
      });

      return {
        ...state,
        betBuilderEvents: eventsToUpdate,
      };
    },
    expandAllCollapsibles(state: BetBuilderState) {
      const eventsToUpdate = cloneDeep(state.betBuilderEvents);
      Object.values(eventsToUpdate).forEach(event => {
        event.isCollapsibleOpen = true; // Expand all events
      });

      return {
        ...state,
        betBuilderEvents: eventsToUpdate,
      };
    },
    updateBetBuilderBet(state: BetBuilderState, payload: any) {
      const eventToUpdate = Object.values(state.betBuilderEvents).find(
        event => event.requestId === payload.requestId
      );

      if (!eventToUpdate) {
        return state;
      }
      // Merge the payload into the eventToUpdate
      const updatedEvent = {
        ...eventToUpdate,
        ...payload,
      };

      const updatedBetBuilderEvents = {
        ...state.betBuilderEvents,
        [updatedEvent.eventId]: updatedEvent,
      };

      return {
        ...state,
        betBuilderEvents: updatedBetBuilderEvents,
      };
    },
    updateBetBuilderEvent(
      state: Record<string, any>,
      payload: {
        eventId: number;
        odds?: number;
        maxStake?: number;
        message?: string;
        submitting?: boolean;
        calculatedOdds?: number;
        errorMessage?: any;
        selections?: any;
        requestId?: string;
      }
    ): any {
      const eventToUpdate = cloneDeep(state.betBuilderEvents[payload.eventId]);

      if (!eventToUpdate) {
        return state;
      }

      // Merge the payload into the eventToUpdate
      const updatedEvent = {
        ...eventToUpdate,
        ...payload,
      };

      const updatedBetBuilderEvents = {
        ...state.betBuilderEvents,
        [payload.eventId]: updatedEvent,
      };

      return {
        ...state,
        betBuilderEvents: updatedBetBuilderEvents,
      };
    },
    updatePricesFromWS(state: BetBuilderState, payload: any) {
      const updatedEvents = cloneDeep(state.betBuilderEvents);
      const { eventId, id: runnerId, aggregatedPrices, status } = payload;
      const currentEvent = updatedEvents[eventId];
      if (!currentEvent) {
        return state;
      }

      const itemToUpdate = currentEvent.betBuilderItems.find(
        item => item.runnerId === runnerId
      );
      if (!itemToUpdate) {
        return state;
      }
      if (aggregatedPrices?.length > 0) {
        itemToUpdate.markets[0].runners[0].aggregatedPrices = aggregatedPrices;
      }
      return {
        ...state,
        betBuilderEvents: updatedEvents,
      };
    },
    updatePricesFromPoll(state: Record<string, any>, payload: any): any {
      const betBuilderEvents = cloneDeep(state.betBuilderEvents);
      const eventToUpdate = betBuilderEvents[payload.eventId];

      for (const priceUpdate of payload) {
        const { runnerId, aggregatedPrices } = priceUpdate;

        if (eventToUpdate) {
          const itemToUpdate = eventToUpdate.betBuilderItems.find(
            (item: { runnerId: number }) => item.runnerId === runnerId
          );
          if (itemToUpdate) {
            itemToUpdate.markets[0].runners[0].aggregatedPrices =
              aggregatedPrices;
          }
        }
      }

      return {
        ...state,
        betBuilderEvents: betBuilderEvents,
      };
    },
  },

  effects: dispatch => ({
    validate: debounce(
      async (payload: { eventId: number }, rootState: Record<string, any>) => {
        const eventId = payload.eventId;
        const currentEvent =
          rootState.betBuilderModel.betBuilderEvents[eventId];

        // Need at least 2 items for a valid betbuilder
        if (currentEvent.betBuilderItems.length < 2) {
          return;
        }
        // If the bet builder model is currently loading, exit the function
        if (rootState.betBuilderModel.loading) {
          return;
        }

        if (!currentEvent || currentEvent.betBuilderItems.length === 0) {
          return;
        }

        // Prepare the validation object with necessary data
        const validationObj = {
          multiplierBetLegs: [] as Array<any>,
          oddsType: localStorage.getItem("oddsType"),
          platform: SessionHelper.isAsianRouteAndSessionView()
            ? "asian"
            : "european",
        };
        // Populate the bet legs array only for the specific event
        for (const item of currentEvent.betBuilderItems) {
          validationObj["multiplierBetLegs"].push({
            odds: item.markets[0].runners[0].aggregatedPrices[0].displayOdds,
            runnerId: item.markets[0].runners[0].id,
            oddsType: item.markets[0].runners[0].aggregatedPrices[0].oddsType,
          });
        }

        try {
          const resp = await api.validateBetBuilder(validationObj);
          if (resp.data) {
            dispatch({
              type: "betBuilderModel/updateBetBuilderEvent",
              payload: {
                eventId,
                maxStake: resp.data.maxStake,
                calculatedOdds: resp.data.outcome.calculatedOdds,
                errorMessage: resp.data.outcome,
                selections: resp.data.selections,
              },
            });
          } else {
            throw new Error("Validation response is empty.");
          }
          return resp;
        } catch (error) {
          console.error("Error occurred during API call:", error);
        }
      },
      300
    ),
    placeBet: async (payload: any, rootState: Record<string, any>) => {
      const eventId = payload.eventId;
      const requestId = uuidv4();
      let currentEvent = rootState.betBuilderModel.betBuilderEvents[eventId];
      const calculatedOdds = payload.calculatedOdds;

      if (!currentEvent) {
        return;
      }
      // Send DECIMAL odds & oddsType for betbuilder & legs, if user has selected Malay odds
      const userOddsType = rootState.userModel?.user?.preferences?.oddsType;
      const isMalayOdds = userOddsType === OddsType.MALAY;
      const oddsType = isMalayOdds ? OddsType.DECIMAL : userOddsType;
      const betBuilderLegs = currentEvent.betBuilderItems.map(item => {
        const oddsType =
          item.markets[0].runners[0].aggregatedPrices[0].oddsType;
        const isMalayOdds = oddsType === OddsType.MALAY;
        const odds = isMalayOdds
          ? item.markets[0].runners[0].aggregatedPrices[0].decimalOdds
          : item.markets[0].runners[0].aggregatedPrices[0].displayOdds;

        return {
          odds: odds,
          runnerId: item.runnerId,
          oddsType: isMalayOdds ? OddsType.DECIMAL : oddsType,
        };
      });
      // add request id before calling the api
      dispatch({
        type: "betBuilderModel/updateBetBuilderEvent",
        payload: {
          eventId,
          submitting: true,
          requestId: requestId,
          message: "",
        },
      });

      try {
        const resp = await placeMultiple(
          betBuilderLegs,
          Number(payload.stake),
          oddsType,
          requestId, // Replace with actual request_id
          calculatedOdds, // Replace with actual oddsTotal if needed
          true
        );

        if (resp) {
          const multipleBetResponse = get(resp, ["bets", 0]);
          const payload = constructPayload(eventId, multipleBetResponse);

          // Update the message and submitting flag
          dispatch({
            type: "betBuilderModel/updateBetBuilderEvent",
            // payload: {
            //   eventId,
            //   message: multipleBetResponse.message,
            //   submitting: false,
            //   requestId: multipleBetResponse.requestId,
            // },
            payload,
          });
        }
      } catch (error) {
        console.error("Error occurred during API call:", error);
        dispatch({
          type: "betBuilderModel/updateBetBuilderEvent",
          payload: {
            eventId,
            submitting: false,
          },
        });
      }
    },
  }),
};

export default BetBuilderModel;
